import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  Divider,
  InputAdornment,
  FormHelperText,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Tab,
  Tabs,
  Avatar,
  Badge,
  useTheme,
  alpha,
  Skeleton,
  styled,
  Fade,
  Zoom
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  LocalOffer as PriceIcon,
  Category as CategoryIcon,
  Image as ImageIcon,
  Refresh as RefreshIcon,
  Token as TokenIcon,
  CloudUpload as CloudUploadIcon,
  Link as LinkIcon,
  DataUsage as DataUsageIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  MoreVert as MoreVertIcon,
  Star as StarIcon,
  Storefront as StorefrontIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import { marketplaceService, tenantTokenService } from '../../../services/skillcoinService';
import { motion } from 'framer-motion';

const AdminMarketplaceManager = () => {
  // State for marketplace items
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  
  // State for filtering and searching
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [listingTypeFilter, setListingTypeFilter] = useState('all');

  // State for the item form dialog
  const [formDialog, setFormDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    tokenId: '',
    listingType: 'physical_item',
    categories: [],
    tags: [],
    status: 'active',
    images: [],
    isGloballyAvailable: false,
    allowNegotiation: false,
    estimatedDeliveryTime: '',
    quantity: 1
  });
  const [formErrors, setFormErrors] = useState({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [categoryInput, setCategoryInput] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [imageAltInput, setImageAltInput] = useState('');
  const [tokens, setTokens] = useState([]);
  const [loadingTokens, setLoadingTokens] = useState(false);

  // File upload states and refs
  const [imageTab, setImageTab] = useState(0); // 0 for URL, 1 for Upload
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);

  // Handle image tab change
  const handleImageTabChange = (event, newValue) => {
    setImageTab(newValue);
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setSelectedFile(file);
    
    // Create a preview URL
    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Handle file upload
  const handleUploadImage = () => {
    if (!selectedFile) return;
    
    setUploading(true);
    
    // Create a base64 string from the file for simplicity
    // In a real application, you might want to upload to a server/storage
    const reader = new FileReader();
    reader.onload = () => {
      // Validate image size before adding
      const img = new Image();
      img.onload = () => {
        // If image is too large (over 800KB as base64), compress it
        if (reader.result.length > 800000) {
          compressImage(selectedFile, 0.7, 800, 800, (compressedImage) => {
            // Add the compressed image to the form
            setFormData({
              ...formData,
              images: [
                ...formData.images,
                {
                  url: compressedImage,
                  alt: selectedFile.name || 'Uploaded image'
                }
              ]
            });
            
            // Reset upload state
            setSelectedFile(null);
            setImagePreview('');
            setUploading(false);
          });
        } else {
          // Add the original image if it's small enough
          setFormData({
            ...formData,
            images: [
              ...formData.images,
              {
                url: reader.result,
                alt: selectedFile.name || 'Uploaded image'
              }
            ]
          });
          
          // Reset upload state
          setSelectedFile(null);
          setImagePreview('');
          setUploading(false);
        }
      };
      
      img.onerror = () => {
        setNotification({
          open: true,
          message: 'Invalid image file. Please select a valid image.',
          severity: 'error'
        });
        setUploading(false);
      };
      
      img.src = reader.result;
    };
    
    reader.onerror = () => {
      setNotification({
        open: true,
        message: 'Failed to process image. Please try again.',
        severity: 'error'
      });
      setUploading(false);
    };
    
    reader.readAsDataURL(selectedFile);
  };
  
  // Helper function to compress images
  const compressImage = (file, quality, maxWidth, maxHeight, callback) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
        
        // Create canvas for compression
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to base64 with compression
        const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
        callback(compressedBase64);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  // Load marketplace items on component mount
  useEffect(() => {
    fetchMarketplaceItems();
  }, [page, rowsPerPage, searchTerm, statusFilter, listingTypeFilter]);

  // Fetch tokens when dialog opens
  useEffect(() => {
    if (formDialog) {
      fetchTokens();
    }
  }, [formDialog]);

  // Fetch marketplace items from API
  const fetchMarketplaceItems = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Log the auth status to help with debugging
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      console.log('Fetching marketplace items with auth token:', !!token);
      
      const params = {
        page: page + 1, // API uses 1-based pagination
        limit: rowsPerPage,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        listingType: listingTypeFilter !== 'all' ? listingTypeFilter : undefined,
        search: searchTerm || undefined
      };

      const response = await marketplaceService.getItems(params);
      console.log('Marketplace items received:', response);
      
      // Handle different response formats to ensure we always get the items
      if (response && response.items) {
        // Format: { items: [], pagination: {} }
        setItems(response.items);
        setTotalItems(response.pagination?.totalItems || response.items.length);
      } else if (response && response.data && Array.isArray(response.data)) {
        // Format: { success: true, data: [] }
        setItems(response.data);
        setTotalItems(response.data.length);
      } else if (response && response.data && response.data.items) {
        // Format: { success: true, data: { items: [], pagination: {} } }
        setItems(response.data.items);
        setTotalItems(response.data.pagination?.totalItems || response.data.items.length);
      } else if (Array.isArray(response)) {
        // Format: []
        setItems(response);
        setTotalItems(response.length);
      } else {
        console.warn('Unexpected response format:', response);
        // Check if response has any array we can use
        const anyArray = Object.values(response || {}).find(val => Array.isArray(val));
        if (anyArray) {
          console.log('Found an array in the response, using it:', anyArray);
          setItems(anyArray);
          setTotalItems(anyArray.length);
        } else {
          console.error('Could not find items array in the response');
          setItems([]);
          setTotalItems(0);
        }
      }
    } catch (err) {
      console.error('Error loading marketplace items:', err);
      // Check for auth related errors
      if (err.response && err.response.status === 401) {
        setError('Authentication error. Please log in again to access marketplace items.');
      } else {
        setError('Failed to load marketplace items. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch available tokens for the tenant
  const fetchTokens = async () => {
    try {
      setLoadingTokens(true);
      console.log('Fetching tokens for marketplace items...');
      
      // Get tenant ID for debugging
      const tenantId = localStorage.getItem('tenantId');
      console.log('Using tenant ID:', tenantId);
      
      const response = await tenantTokenService.getTenantToken();
      console.log('Fetched tokens response:', response);
      
      // Process different response formats based on the actual API response structure
      if (response && response.data && response.data.token) {
        // Format when API returns {success: true, data: {token: {...}, stats: {...}}}
        const tokenData = response.data.token;
        setTokens([tokenData]);
        console.log('Setting token from response.data.token:', tokenData);
        
        // Auto-select the token
        if (!formData.tokenId && tokenData._id) {
          setFormData(prev => ({
            ...prev,
            tokenId: tokenData._id
          }));
        }
      } else if (response && response.data) {
        // Format when the API returns {success: true, data: {...}}
        setTokens([response.data]);
        console.log('Setting token from response.data:', response.data);
        
        // Auto-select the token
        if (!formData.tokenId && response.data._id) {
          setFormData(prev => ({
            ...prev,
            tokenId: response.data._id
          }));
        }
      } else if (response && response.success && response.data) {
        // Format when the API returns nested data
        setTokens([response.data]);
        console.log('Setting token from nested data:', response.data);
        
        // Auto-select the token
        if (!formData.tokenId && response.data._id) {
          setFormData(prev => ({
            ...prev,
            tokenId: response.data._id
          }));
        }
      } else if (Array.isArray(response)) {
        // Format when the API returns an array directly
        setTokens(response);
        console.log('Setting tokens from array:', response);
        
        // Auto-select the first token if there's only one
        if (response.length === 1 && !formData.tokenId) {
          setFormData(prev => ({
            ...prev,
            tokenId: response[0]._id
          }));
        }
      } else if (response && Array.isArray(response.data)) {
        // Format when the API returns {data: []}
        setTokens(response.data);
        console.log('Setting tokens from response.data array:', response.data);
        
        // Auto-select the first token if there's only one
        if (response.data.length === 1 && !formData.tokenId) {
          setFormData(prev => ({
            ...prev,
            tokenId: response.data[0]._id
          }));
        }
      } else {
        console.warn('Unexpected token response format:', response);
        setTokens([]);
      }
    } catch (err) {
      console.error('Error fetching tokens:', err);
      setNotification({
        open: true,
        message: 'Failed to load tokens. Please try again later.',
        severity: 'error'
      });
      setTokens([]);
    } finally {
      setLoadingTokens(false);
    }
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle form input change
  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    // Clear error for this field if it exists
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
  };

  // Add a category to the form
  const handleAddCategory = () => {
    if (categoryInput.trim() && !formData.categories.includes(categoryInput.trim())) {
      setFormData({
        ...formData,
        categories: [...formData.categories, categoryInput.trim()]
      });
      setCategoryInput('');
    }
  };

  // Remove a category from the form
  const handleRemoveCategory = (category) => {
    setFormData({
      ...formData,
      categories: formData.categories.filter(c => c !== category)
    });
  };

  // Add a tag to the form
  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()]
      });
      setTagInput('');
    }
  };

  // Remove a tag from the form
  const handleRemoveTag = (tag) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(t => t !== tag)
    });
  };

  // Add an image to the form
  const handleAddImage = () => {
    if (imageUrlInput.trim()) {
      setFormData({
        ...formData,
        images: [
          ...formData.images,
          {
            url: imageUrlInput.trim(),
            alt: imageAltInput.trim() || imageUrlInput.trim()
          }
        ]
      });
      setImageUrlInput('');
      setImageAltInput('');
    }
  };

  // Remove an image from the form
  const handleRemoveImage = (index) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index)
    });
  };

  // Validate the form
  const validateForm = () => {
    const errors = {};

    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    }

    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }
    
    if (!formData.price || parseFloat(formData.price) <= 0) {
      errors.price = 'Price must be greater than 0';
    }

    if (!formData.listingType) {
      errors.listingType = 'Listing type is required';
    }    

    if (!formData.quantity || parseInt(formData.quantity) <= 0) {
      errors.quantity = 'Quantity must be greater than 0';
    }

    if (!formData.tokenId) {
      errors.tokenId = 'Please select a token for this marketplace item';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmitForm = async (values, formikHelpers) => {
    try {
      console.log('Form values being submitted:', values);
      
      // Make sure quantity is properly handled
      const formData = {
        ...values,
        quantity: Number(values.quantity) || 10, // Ensure quantity is a number with a fallback
        price: Number(values.price) || 0
      };
      
      // Add validation to prevent submission with invalid quantity
      if (formData.quantity < 1) {
        setNotification({
          open: true,
          message: 'Quantity must be at least 1',
          severity: 'error'
        });
        return;
      }
      
      console.log('Formatted form data:', formData);

      setLoading(true);
      try {
        // Get tenant ID
        const tenantId = localStorage.getItem('tenantId');
        if (!tenantId) {
          setNotification({
            open: true,
            message: 'Tenant ID is required but was not found. Please make sure you are logged in properly.',
            severity: 'error'
          });
          setLoading(false);
          return;
        }

        // Format the data in the structure expected by the API
        const formattedData = {
          title: formData.title,
          description: formData.description,
          price: parseFloat(formData.price),
          tokenId: formData.tokenId,
          listingType: formData.listingType,
          status: formData.status,
          quantity: parseInt(formData.quantity),
          categories: formData.categories,
          tags: formData.tags,
          images: formData.images,
          isGloballyAvailable: formData.isGloballyAvailable,
          allowNegotiation: formData.allowNegotiation,
          tenantId: tenantId,
          estimatedDeliveryTime: formData.estimatedDeliveryTime 
            ? parseInt(formData.estimatedDeliveryTime) 
            : undefined
        };
        
        console.log('Sending marketplace item data:', formattedData);
        
        let response;
        if (isEditing) {
          response = await marketplaceService.updateItem(formData._id, formattedData);
        } else {
          response = await marketplaceService.createItem(formattedData);
        }

        if (response) {
          setNotification({
            open: true,
            message: `Item ${isEditing ? 'updated' : 'created'} successfully`,
            severity: 'success'
          });
          setFormDialog(false);
          fetchMarketplaceItems();
        } else {
          setNotification({
            open: true,
            message: `Failed to ${isEditing ? 'update' : 'create'} item`,
            severity: 'error'
          });
        }
      } catch (err) {
        console.error('Error in marketplace item operation:', err);
        setNotification({
          open: true,
          message: err.message || `An error occurred while ${isEditing ? 'updating' : 'creating'} the marketplace item`,
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error in form submission:', error);
    }
  };

  // Open the form dialog for creating a new item
  const handleOpenForm = (item = null) => {
    setIsEditing(!!item);
    if (item) {
      // When editing, ensure quantity is properly set
      setFormData({
        title: item.title || '',
        description: item.description || '',
        price: item.price || 0,
        quantity: item.quantity || 10, // Use 10 as fallback if not set
        listingType: item.listingType || 'product',
        tokenId: item.tokenId || '',
        categories: item.categories || [],
        tags: item.tags || [],
        status: item.status || 'active',
        images: item.images || [],
        isGloballyAvailable: item.isGloballyAvailable || false,
        allowNegotiation: item.allowNegotiation || false,
        estimatedDeliveryTime: item.estimatedDeliveryTime || ''
      });
    } else {
      // For new items, initialize with defaults including explicit quantity
      setFormData({
        title: '',
        description: '',
        price: 0,
        quantity: 10, // Set default to 10 for new items
        listingType: 'product',
        tokenId: '',
        categories: [],
        tags: [],
        status: 'active',
        images: [],
        isGloballyAvailable: false,
        allowNegotiation: false,
        estimatedDeliveryTime: ''
      });
    }
    setFormDialog(true);
  };

  // Open the delete confirmation dialog
  const handleOpenDeleteDialog = (item) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  // Delete an item
  const handleDeleteItem = async () => {
    if (!itemToDelete) return;
    
    setLoading(true);
    try {
      const result = await marketplaceService.deleteItem(itemToDelete._id);
      
      if (result) {
        setNotification({
          open: true,
          message: 'Item deleted successfully',
          severity: 'success'
        });
        fetchMarketplaceItems();
      } else {
        setNotification({
          open: true,
          message: 'Failed to delete item',
          severity: 'error'
        });
      }
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || 'An error occurred while deleting the item',
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    }
  };

  // Update item status
  const handleUpdateStatus = async (itemId, newStatus) => {
    setLoading(true);
    try {
      const result = await marketplaceService.updateItemStatus(itemId, newStatus);
      if (result) {
        setNotification({
          open: true,
          message: 'Item status updated successfully',
          severity: 'success'
        });
        fetchMarketplaceItems();
      } else {
        setNotification({
          open: true,
          message: 'Failed to update item status',
          severity: 'error'
        });
      }
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || 'An error occurred while updating the item status',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle closing notification
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // Get status color for display
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'paused':
        return 'warning';
      case 'draft':
        return 'default';
      case 'under_review':
        return 'info';
      case 'rejected':
        return 'error';
      case 'completed':
        return 'primary';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        {/* Header */}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h4" component="h1">
              Marketplace Management
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenForm()}
            >
              Add New Item
            </Button>
          </Box>
          <Divider />
        </Grid>

        {/* Filters and Search */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search marketplace items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="status-filter-label">Status</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="paused">Paused</MenuItem>
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="under_review">Under Review</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="listing-type-filter-label">Listing Type</InputLabel>
                  <Select
                    labelId="listing-type-filter-label"
                    value={listingTypeFilter}
                    onChange={(e) => setListingTypeFilter(e.target.value)}
                    label="Listing Type"
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="service">Service</MenuItem>
                    <MenuItem value="digital_content">Digital Content</MenuItem>
                    <MenuItem value="physical_item">Physical Item</MenuItem>
                    <MenuItem value="event">Event</MenuItem>
                    <MenuItem value="consultation">Consultation</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('all');
                    setListingTypeFilter('all');
                    setPage(0);
                  }}
                >
                  Reset
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Items Table */}
        <Grid item xs={12}>
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            {error ? (
              <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
            ) : loading && items.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
            ) : items.length === 0 ? (
              <Box p={3} textAlign="center">
                <Typography variant="h6">No items found</Typography>
                <Typography variant="body2" color="text.secondary">
                  Try adjusting your filters or create a new marketplace item.
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer sx={{ maxHeight: 440 }}>
                  <Table stickyHeader aria-label="marketplace items table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Price</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Categories</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((item) => (
                        <TableRow key={item._id} hover>
                          <TableCell component="th" scope="row">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {item.images && item.images[0] ? (
                                <Box sx={{ position: 'relative', mr: 2 }}>
                                  <Box
                                    component="img"
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      borderRadius: 1,
                                      objectFit: 'cover',
                                      display: 'block'
                                    }}
                                    src={item.images[0].url}
                                    alt={item.images[0].alt || item.title}
                                    onError={(e) => {
                                      console.log('Image failed to load:', item.images[0].url);
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                  <Box 
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: 40,
                                      height: 40,
                                      borderRadius: 1,
                                      bgcolor: 'action.disabledBackground',
                                      display: 'none',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    <ImageIcon color="action" />
                                  </Box>
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 1,
                                    mr: 2,
                                    bgcolor: 'action.disabledBackground',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <ImageIcon color="action" />
                                </Box>
                              )}
                              <Typography variant="body2">{item.title}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.listingType.replace('_', ' ')}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <PriceIcon fontSize="small" sx={{ mr: 0.5 }} color="primary" />
                              <Typography variant="body2">{item.price.toFixed(2)} tokens</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.status.replace('_', ' ')}
                              size="small"
                              color={getStatusColor(item.status)}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {item.categories && item.categories.length > 0 ? (
                                item.categories.slice(0, 2).map((category, index) => (
                                  <Chip
                                    key={index}
                                    label={category}
                                    size="small"
                                    variant="outlined"
                                  />
                                ))
                              ) : (
                                <Typography variant="body2" color="text.secondary">No categories</Typography>
                              )}
                              {item.categories && item.categories.length > 2 && (
                                <Chip
                                  label={`+${item.categories.length - 2}`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <Tooltip title="Edit">
                                <IconButton
                                  color="primary"
                                  onClick={() => handleOpenForm(item)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              {item.status === 'active' ? (
                                <Tooltip title="Pause">
                                  <IconButton
                                    color="warning"
                                    onClick={() => handleUpdateStatus(item._id, 'paused')}
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </Tooltip>
                              ) : item.status === 'paused' ? (
                                <Tooltip title="Activate">
                                  <IconButton
                                    color="success"
                                    onClick={() => handleUpdateStatus(item._id, 'active')}
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                              <Tooltip title="Delete">
                                <IconButton
                                  color="error"
                                  onClick={() => handleOpenDeleteDialog(item)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={totalItems}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Item Form Dialog */}
      <Dialog
        open={formDialog}
        onClose={() => setFormDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {isEditing ? 'Edit Marketplace Item' : 'Create New Marketplace Item'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Basic Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Title"
                name="title"
                value={formData.title}
                onChange={handleFormChange}
                fullWidth
                error={!!formErrors.title}
                helperText={formErrors.title}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required error={!!formErrors.listingType}>
                <InputLabel id="listing-type-label">Listing Type</InputLabel>
                <Select
                  labelId="listing-type-label"
                  name="listingType"
                  value={formData.listingType}
                  onChange={handleFormChange}
                  label="Listing Type"
                >
                  <MenuItem value="service">Service</MenuItem>
                  <MenuItem value="digital_content">Digital Content</MenuItem>
                  <MenuItem value="physical_item">Physical Item</MenuItem>
                  <MenuItem value="event">Event</MenuItem>
                  <MenuItem value="consultation">Consultation</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
                {formErrors.listingType && (
                  <FormHelperText>{formErrors.listingType}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                fullWidth
                multiline
                rows={4}
                error={!!formErrors.description}
                helperText={formErrors.description}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Price (in tokens)"
                name="price"
                type="number"
                value={formData.price}
                onChange={handleFormChange}
                fullWidth
                error={!!formErrors.price}
                helperText={formErrors.price}
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start"><PriceIcon /></InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Quantity Available"
                name="quantity"
                type="number"
                value={formData.quantity}
                onChange={handleFormChange}
                fullWidth
                error={!!formErrors.quantity}
                helperText={formErrors.quantity}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Estimated Delivery Time (hours)"
                name="estimatedDeliveryTime"
                type="number"
                value={formData.estimatedDeliveryTime}
                onChange={handleFormChange}
                fullWidth
                error={!!formErrors.estimatedDeliveryTime}
                helperText={formErrors.estimatedDeliveryTime}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  name="status"
                  value={formData.status}
                  onChange={handleFormChange}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="paused">Paused</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="under_review">Under Review</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isGloballyAvailable}
                    onChange={handleFormChange}
                    name="isGloballyAvailable"
                  />
                }
                label="Available Globally (Across Tenants)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.allowNegotiation}
                    onChange={handleFormChange}
                    name="allowNegotiation"
                  />
                }
                label="Allow Price Negotiation"
              />
            </Grid>

            {/* Token Selection */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Token Selection
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required error={!!formErrors.tokenId}>
                <InputLabel id="token-id-label">Select Token</InputLabel>
                <Select
                  labelId="token-id-label"
                  name="tokenId"
                  value={formData.tokenId}
                  onChange={handleFormChange}
                  label="Select Token"
                  disabled={loadingTokens}
                  startAdornment={
                    <InputAdornment position="start">
                      <TokenIcon />
                    </InputAdornment>
                  }
                >
                  {loadingTokens ? (
                    <MenuItem value="">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Loading tokens...
                      </Box>
                    </MenuItem>
                  ) : tokens.length === 0 ? (
                    <MenuItem value="">No tokens available</MenuItem>
                  ) : (
                    tokens.map((token) => (
                      <MenuItem key={token._id} value={token._id}>
                        {token.name} ({token.symbol})
                      </MenuItem>
                    ))
                  )}
                </Select>
                {formErrors.tokenId && (
                  <FormHelperText>{formErrors.tokenId}</FormHelperText>
                )}
                {!loadingTokens && tokens.length === 0 && (
                  <FormHelperText>
                    No tokens found. Please create a token before creating marketplace items.
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Categories */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Categories and Tags
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', mb: 1 }}>
                  <TextField
                    label="Add Category"
                    value={categoryInput}
                    onChange={(e) => setCategoryInput(e.target.value)}
                    fullWidth
                    size="small"
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddCategory}
                    disabled={!categoryInput.trim()}
                    sx={{ ml: 1 }}
                  >
                    Add
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {formData.categories.map((category, index) => (
                    <Chip
                      key={index}
                      label={category}
                      onDelete={() => handleRemoveCategory(category)}
                      color="primary"
                      variant="outlined"
                      icon={<CategoryIcon />}
                    />
                  ))}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', mb: 1 }}>
                  <TextField
                    label="Add Tag"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    fullWidth
                    size="small"
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddTag}
                    disabled={!tagInput.trim()}
                    sx={{ ml: 1 }}
                  >
                    Add
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {formData.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                      color="secondary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* Images */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Images
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Tabs
                value={imageTab}
                onChange={handleImageTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
              >
                <Tab label="Add via URL" />
                <Tab label="Upload Image" />
              </Tabs>
              {imageTab === 0 && (
                <Box sx={{ mb: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={5}>
                      <TextField
                        label="Image URL"
                        value={imageUrlInput}
                        onChange={(e) => setImageUrlInput(e.target.value)}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        label="Image Alt Text"
                        value={imageAltInput}
                        onChange={(e) => setImageAltInput(e.target.value)}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Button
                        variant="contained"
                        onClick={handleAddImage}
                        disabled={!imageUrlInput.trim()}
                        fullWidth
                      >
                        Add Image
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}
              {imageTab === 1 && (
                <Box sx={{ mb: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={10}>
                      <Button
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        onClick={() => fileInputRef.current.click()}
                        fullWidth
                      >
                        {selectedFile ? selectedFile.name : 'Choose File'}
                      </Button>
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                        accept="image/*"
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Button
                        variant="contained"
                        onClick={handleUploadImage}
                        disabled={!selectedFile || uploading}
                        fullWidth
                      >
                        {uploading ? <CircularProgress size={24} /> : 'Upload'}
                      </Button>
                    </Grid>
                  </Grid>
                  {imagePreview && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <Typography variant="caption">Preview:</Typography>
                      <Box
                        component="img"
                        src={imagePreview}
                        alt="Preview"
                        sx={{ maxWidth: '100%', maxHeight: 200, mt: 1 }}
                      />
                    </Box>
                  )}
                </Box>
              )}
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.images.map((image, index) => (
                  <Card key={index} sx={{ width: 150, position: 'relative' }}>
                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                      <Box
                        component="img"
                        sx={{
                          width: '100%',
                          height: 100,
                          objectFit: 'cover',
                          borderRadius: 1,
                          mb: 1
                        }}
                        src={image.url}
                        alt={image.alt}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/150?text=Error';
                        }}
                      />
                      <Typography variant="caption" noWrap>
                        {image.alt}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveImage(index)}
                        sx={{
                          position: 'absolute',
                          top: 5,
                          right: 5,
                          bgcolor: 'rgba(255,255,255,0.7)',
                          '&:hover': {
                            bgcolor: 'rgba(255,255,255,0.9)',
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSubmitForm}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : isEditing ? 'Update Item' : 'Create Item'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{itemToDelete?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteItem}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminMarketplaceManager;